import { forwardRef, ForwardedRef } from 'react';
import { ConfigProvider, Menu, MenuProps, MenuRef } from 'antd';
import type { ThemeConfig } from 'antd/es/config-provider/context';

interface CustomMenuProps extends MenuProps {
  // Theme customization props
  itemColor?: string;
  itemSelectedColor?: string;
  itemSelectedBg?: string;
  itemHoverColor?: string;
  itemHoverBg?: string;
  popupBg?: string;

  // Additional styling props
  borderRadius?: number;
  fontSize?: number;

  // Layout variant
  variant?: 'light' | 'dark' | 'custom';
}

const themeVariants = {
  light: {
    itemColor: '#666666',
    itemSelectedColor: '#1890ff',
    itemSelectedBg: '#e6f7ff',
    itemHoverColor: '#1890ff',
    itemHoverBg: '#f5f5f5',
    popupBg: '#ffffff',
  },
  dark: {
    itemColor: 'rgba(255, 255, 255, 0.65)',
    itemSelectedColor: '#ffffff',
    itemSelectedBg: '#1890ff',
    itemHoverColor: '#ffffff',
    itemHoverBg: 'rgba(255, 255, 255, 0.08)',
    popupBg: '#18181B',
  },
  custom: {
    itemColor: '#777777',
    itemSelectedColor: '#ffffff',
    itemSelectedBg: '#3c91e6',
    itemHoverColor: '#3c91e6',
    itemHoverBg: '#f0f6ff',
    popupBg: '#18181B',
  },
};

export const CustomSidebarMenu = forwardRef<MenuRef, CustomMenuProps>(
  function CustomSidebarMenu(
    {
      variant = 'custom',
      itemColor,
      itemSelectedColor,
      itemSelectedBg,
      itemHoverColor,
      itemHoverBg,
      popupBg,
      borderRadius = 8,
      fontSize = 16,
      ...menuProps
    }: CustomMenuProps,
    ref: ForwardedRef<MenuRef>
  ) {
    // Get base theme from variant
    const baseTheme = themeVariants[variant];

    // Override with custom props if provided
    const themeConfig: ThemeConfig = {
      components: {
        Menu: {
          itemColor: itemColor || baseTheme.itemColor,
          itemSelectedColor: itemSelectedColor || baseTheme.itemSelectedColor,
          itemSelectedBg: itemSelectedBg || baseTheme.itemSelectedBg,
          itemHoverColor: itemHoverColor || baseTheme.itemHoverColor,
          itemHoverBg: itemHoverBg || baseTheme.itemHoverBg,
          itemActiveBg: itemSelectedBg || baseTheme.itemSelectedBg,
          itemBorderRadius: borderRadius,
          fontSize: fontSize,
          popupBg: popupBg || baseTheme.popupBg || '#18181B',

          // Layout adjustments
          itemPaddingInline: 16,
          itemMarginInline: 12,
          itemHeight: 48,

          // Submenu
          subMenuItemBg: 'transparent',
          subMenuItemSelectedColor: itemSelectedColor || baseTheme.itemSelectedColor,
        },
      },
    };

    return (
      <ConfigProvider theme={themeConfig}>
        <Menu ref={ref} {...menuProps} />
      </ConfigProvider>
    );
  }
);

export default CustomSidebarMenu;
